import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createBlock as _createBlock, createVNode as _createVNode, vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives } from "vue"

import { computed, ref, toRefs, watch } from 'vue'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import mapboxgl, { FillLayer, GeoJSONSourceRaw, LineLayer } from 'mapbox-gl'
import GeojsonLayer, { GeojsonLayerEvent } from '@/components/map/mapbox-layers/GeojsonLayer.vue'
import MapPopupNew from '../../popups/MapPopupNew.vue'
import { createSmallSubtitle, createSmallTitle } from '@/components/Builder'
import { formatNumber } from '@/utils/math'
import useMapbox from '@/composables/useMapbox'
import { ZoneGeoJsonFeature } from '@/services/zones.service'

interface PROPS {
  before: string
}
type ZoneTypeSource = {
  id: string
  name: string
  source: GeoJSONSourceRaw
}

type ZoneSource = {
  category: string
  zoneTypes: ZoneTypeSource[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LayerLandZone',
  props: {
    before: {}
  },
  setup(__props: any) {

const piniaStore = useGeneralStore()
const store = useStore()
const { map } = useMapbox()

// Props
const props = __props
const { before } = toRefs(props)

// Data
const hoveredZoneId = ref<string | null>(null)
const popup = new mapboxgl.Popup({ closeButton: false, offset: 5 })
const popupRef = ref<HTMLElement | null>(null)
const popupSubtitle = ref('')
const popupTitle = ref('')

const zoneSource = ref<ZoneSource[]>([])

const clickedItemType = computed<string>(() => store.state.clickedItemType)
const currentZoom = computed<number>(() => store.state.currentZoom)
const filteredLayers = computed<any[]>(() => store.state.mapview.filteredLayers)
const isDrawPointActive = computed<boolean>(() => store.getters.isDrawPointActive)
const isMeasurementActive = computed<boolean>(() => store.getters.isMeasurementActive)
const outdoorZonesForSelectedRealEstate = computed<ZoneGeoJsonFeature[]>(
  () => store.state.mapview.outdoorZonesForSelectedRealEstate
)
const selectedZone = computed(() => piniaStore.selectedZone)
const selectedAttachmentEntityId = computed(() => {
  return piniaStore.selectedAttachment?.entityId ?? ''
})
const selectedTicketEntityId = computed(() => {
  return piniaStore.selectedTicket?.entityConnection?.length &&
    piniaStore.selectedTicket?.entityConnection[0].id
    ? piniaStore.selectedTicket.entityConnection[0].id
    : ''
})
const zoneCategoriesForSelectedRealEstate = computed<any[]>(
  () => store.state.mapview.zoneCategoriesForSelectedRealEstate
)

const showLines = computed<boolean>(() => currentZoom.value > 14)
const visibleZoneTypes = computed<string[]>(() => {
  let visibleZoneTypes: string[] = []
  filteredLayers.value.forEach((zoneCategory) => {
    zoneCategory.types.forEach((type) => {
      if (type.visible) visibleZoneTypes.push(type.id)
    })
    if (zoneCategory.visible) visibleZoneTypes.push(zoneCategory.id)
  })
  return visibleZoneTypes
})

// Watch
watch(hoveredZoneId, (newId, oldId) => {
  setHoverFeatureState(newId, true)
  setHoverFeatureState(oldId, false)
})
watch(
  outdoorZonesForSelectedRealEstate,
  (outdoorZones) => {
    zoneSource.value = []
    if (!outdoorZonesForSelectedRealEstate.value.length) return
    const uniqueZoneCategoriesForRealEstate = [
      ...new Set(outdoorZones.map((zone) => zone.properties.categoryId))
    ]
    const preparedOutdoorZonesSource: any[] = []
    uniqueZoneCategoriesForRealEstate.forEach((categoryId) => {
      const featuresForCategory = outdoorZonesForSelectedRealEstate.value.filter(
        (feature) => feature.properties.categoryId === categoryId
      )
      const fullCategory = zoneCategoriesForSelectedRealEstate.value.find(
        (category) => category.id === categoryId
      )
      const outdoorZoneTypesForCategory = generateZoneTypes(featuresForCategory)
      preparedOutdoorZonesSource.push({
        category: fullCategory.name,
        zoneTypes: outdoorZoneTypesForCategory
      })
    })
    zoneSource.value = preparedOutdoorZonesSource
  },
  { immediate: true }
)

// Methods
function getLandzoneLayerId(category: string, type: string, typeId: string) {
  return `layer-landzone-${category}-${type}-${typeId}`
}
function getLandzoneSourceId(category: string, type: string, typeId: string) {
  return `source-landzone-${category}-${type}-${typeId}`
}
function handleClick(event: GeojsonLayerEvent) {
  if (isMeasurementActive.value || isDrawPointActive.value || !isFirstLayer(event)) return
  const renderedFeatures = map?.queryRenderedFeatures(event.point)
  if (!renderedFeatures) return
  const zone = outdoorZonesForSelectedRealEstate.value.find((zoneFeature) =>
    renderedFeatures.find((renderedFeature) => zoneFeature.id === renderedFeature?.properties?.id)
  )
  if (!zone) return
  piniaStore.setSelectedLine(null)
  store.commit('RESET_CURRENT_ROOM')
  store.dispatch('CHANGE_POI', { poi: null })
  piniaStore.setSelectedZone(zone.properties)
  store.commit('SET_CLICKED_ITEM_TYPE', { type: 'zone' })
}
function handleMouseLeave(event: GeojsonLayerEvent) {
  if (isMeasurementActive.value || isDrawPointActive.value) return
  setCursor('')
  updateHoverId(event)
}
function isFirstLayer(event: GeojsonLayerEvent) {
  const renderedFeatures = map?.queryRenderedFeatures(event.point)
  if (renderedFeatures == null || !renderedFeatures.length) return false
  return renderedFeatures[0].source.startsWith('source-landzone')
}
function layerZoneStyle(zoneCategory: string, zoneType: ZoneTypeSource): FillLayer {
  const selectedZoneId =
    clickedItemType.value === 'zone' && selectedZone.value?.id ? selectedZone.value.id : ''
  return {
    id: getLandzoneLayerId(zoneCategory, zoneType.name, zoneType.id),
    type: 'fill',
    layout: {
      visibility:
        visibleZoneTypes.value.indexOf(zoneType.id) !== -1 &&
        (clickedItemType.value === 'realEstate' ||
          clickedItemType.value === 'poi' ||
          clickedItemType.value === 'line' ||
          clickedItemType.value === 'zone')
          ? 'visible'
          : 'none'
    },
    paint: {
      'fill-color': [
        'case',
        ['boolean', ['==', selectedAttachmentEntityId.value, ['get', 'layerId']], false],
        '#FADB14',
        ['boolean', ['==', selectedTicketEntityId.value, ['get', 'layerId']], false],
        '#FADB14',
        ['boolean', ['get', 'color'], false],
        '#969696',
        ['get', 'color']
      ],
      'fill-opacity': [
        'case',
        ['boolean', ['==', selectedAttachmentEntityId.value, ['get', 'layerId']], false],
        0.9,
        ['boolean', ['==', selectedTicketEntityId.value, ['get', 'layerId']], false],
        0.9,
        ['boolean', ['==', hoveredZoneId.value, ['get', 'id']], false],
        0.3,
        ['boolean', ['==', selectedZoneId, ['get', 'id']], false],
        0.3,
        selectedAttachmentEntityId.value.length || selectedTicketEntityId.value.length ? 0.1 : 0.7
      ]
    }
  }
}
function layerDashDoubleDotLinesStyle(zoneCategory: string, zoneType: ZoneTypeSource): LineLayer {
  return {
    id: getLandzoneLayerId(zoneCategory, zoneType.name, zoneType.id) + '-lines-dashdoubledot',
    type: 'line',
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
      visibility:
        showLines.value &&
        visibleZoneTypes.value.indexOf(zoneType.id) !== -1 &&
        (clickedItemType.value === 'realEstate' ||
          clickedItemType.value === 'poi' ||
          clickedItemType.value === 'line' ||
          clickedItemType.value === 'zone')
          ? 'visible'
          : 'none'
    },
    paint: {
      'line-color': '#fff',
      'line-dasharray': [5, 2.5, 0, 2.5, 0, 2.5],
      'line-opacity': [
        'case',
        ['boolean', ['==', hoveredZoneId.value, ['get', 'id']], false],
        0,
        1
      ],
      'line-width': {
        base: 10,
        stops: [
          [14, 1],
          [18, 4],
          [22, 20]
        ]
      }
    }
  }
}
function layerLinesStyle(zoneCategory, zoneType): LineLayer {
  const selectedZoneId =
    clickedItemType.value === 'zone' && selectedZone.value?.id ? selectedZone.value.id : ''
  return {
    id: getLandzoneLayerId(zoneCategory, zoneType.name, zoneType.id) + '-lines',
    type: 'line',
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
      visibility:
        showLines.value &&
        visibleZoneTypes.value.indexOf(zoneType.id) !== -1 &&
        (clickedItemType.value === 'realEstate' ||
          clickedItemType.value === 'poi' ||
          clickedItemType.value === 'line' ||
          clickedItemType.value === 'zone')
          ? 'visible'
          : 'none'
    },
    paint: {
      'line-color': ['case', ['boolean', ['get', 'color'], false], '#FFF', ['get', 'color']],
      'line-opacity': [
        'case',
        ['boolean', ['==', selectedAttachmentEntityId.value, ['get', 'layerId']], false],
        0,
        ['boolean', ['==', selectedTicketEntityId.value, ['get', 'layerId']], false],
        0,
        ['boolean', ['==', hoveredZoneId.value, ['get', 'id']], false],
        0.7,
        ['boolean', ['==', selectedZoneId, ['get', 'id']], false],
        0.7,
        0
      ],
      'line-width': {
        base: 10,
        stops: [
          [14, 2],
          [18, 4],
          [22, 40]
        ]
      }
    }
  }
}
function resetHoverId() {
  hoveredZoneId.value = null
  popup.remove()
}
function setCursor(cursorType: string) {
  if (isMeasurementActive.value || isDrawPointActive.value) return
  if (map) map.getCanvas().style.cursor = cursorType
}
function setHoverFeatureState(featureId: string | null, hasState: boolean) {
  if (!featureId) return
  const fullFeature = outdoorZonesForSelectedRealEstate.value.find((zone) => zone.id === featureId)
  if (fullFeature) {
    const fullCategory = zoneCategoriesForSelectedRealEstate.value.find(
      (category) => category.id === fullFeature.properties.categoryId
    )
    const zoneTypes = zoneCategoriesForSelectedRealEstate.value
      .map((x) => x.types)
      .flatMap((x) => x)
    const fullType = zoneTypes.find((type) => type.id === fullFeature.properties.typeId)
    map?.setFeatureState(
      {
        source: getLandzoneSourceId(fullCategory.name, fullType.name, fullType.id),
        id: featureId
      },
      { hover: hasState }
    )
  }
}
function showPopup(event: GeojsonLayerEvent) {
  if (!popupRef.value) return
  // @ts-ignore
  popup.setLngLat(event.lngLat).setDOMContent(popupRef.value.$el).addTo(map)
  popup.trackPointer()
}
function generateZoneTypes(features: any[]) {
  features.forEach((zone) => (zone.properties.id = zone.id))
  const sortedZoneTypesForCategory: ZoneTypeSource[] = []
  const uniqueZoneTypesForCategory = [...new Set(features.map((zone) => zone.properties.typeId))]
  uniqueZoneTypesForCategory.forEach((zoneType) => {
    const zonesForType = features.filter((zone) => zone.properties.typeId === zoneType)
    const zoneTypes = zoneCategoriesForSelectedRealEstate.value
      .map((x) => x.types)
      .flatMap((x) => x)
    const zone = zoneTypes.find((type) => type.id === zoneType)
    sortedZoneTypesForCategory.push({
      id: zone.id,
      name: zone.name,
      source: {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: zonesForType
        }
      }
    })
  })
  return sortedZoneTypesForCategory
}
function updateHoverId(event: GeojsonLayerEvent) {
  const features = event.features
  if (
    isMeasurementActive.value ||
    isDrawPointActive.value ||
    !isFirstLayer(event) ||
    !features ||
    !features.length
  ) {
    resetHoverId()
    return
  }
  if (hoveredZoneId.value === features[0].properties?.id) return
  hoveredZoneId.value = features[0].properties?.id
  popupTitle.value = features[0].properties?.name
  popupSubtitle.value = formatNumber(features[0].properties?.area, 1) + ' m²'
  showPopup(event)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(zoneSource.value, (zoneCategory, categoryIndex) => {
      return (_openBlock(), _createElementBlock("div", { key: categoryIndex }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(zoneCategory.zoneTypes, (zoneType, typeIndex) => {
          return (_openBlock(), _createBlock(GeojsonLayer, {
            key: typeIndex,
            before: _unref(before),
            layer: layerZoneStyle(zoneCategory.category, zoneType),
            source: zoneType.source,
            sourceId: getLandzoneSourceId(zoneCategory.category, zoneType.name, zoneType.id),
            onLayerClicked: handleClick,
            onLayerMouseEnter: _cache[0] || (_cache[0] = ($event: any) => (setCursor('pointer'))),
            onLayerMouseLeave: handleMouseLeave,
            onLayerMouseMove: updateHoverId
          }, null, 8, ["before", "layer", "source", "sourceId"]))
        }), 128)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(zoneCategory.zoneTypes, (zoneType, typeIndex) => {
          return (_openBlock(), _createBlock(GeojsonLayer, {
            key: typeIndex,
            before: _unref(before),
            layer: layerDashDoubleDotLinesStyle(zoneCategory.category, zoneType),
            source: zoneType.source,
            sourceId: getLandzoneSourceId(zoneCategory.category, zoneType.name, zoneType.id)
          }, null, 8, ["before", "layer", "source", "sourceId"]))
        }), 128)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(zoneCategory.zoneTypes, (zoneType, typeIndex) => {
          return (_openBlock(), _createBlock(GeojsonLayer, {
            key: typeIndex,
            before: _unref(before),
            layer: layerLinesStyle(zoneCategory.category, zoneType),
            layerId: getLandzoneLayerId(zoneCategory.category, zoneType.name, zoneType.id) + '-lines',
            source: zoneType.source,
            sourceId: zoneType.id
          }, null, 8, ["before", "layer", "layerId", "source", "sourceId"]))
        }), 128))
      ]))
    }), 128)),
    _withDirectives(_createElementVNode("div", null, [
      _createVNode(MapPopupNew, {
        ref_key: "popupRef",
        ref: popupRef,
        rows: [_unref(createSmallTitle)(popupTitle.value), _unref(createSmallSubtitle)(popupSubtitle.value)]
      }, null, 8, ["rows"])
    ], 512), [
      [_vShow, false]
    ])
  ], 64))
}
}

})